import React, { useState, useEffect } from "react";
import { Form, Input, Select, DatePicker, Button, Row, Col } from "antd";
import {
    SearchOutlined,
    ClearOutlined,
    FilterOutlined,
    UpOutlined,
    PhoneOutlined,
    IdcardOutlined,
    ClockCircleOutlined,
    CalendarOutlined
} from "@ant-design/icons";
// dayjs used by antd v5 DatePicker and quick filter date computation
import dayjs from "dayjs";

const { Option } = Select;
const { RangePicker } = DatePicker;

// Quick filter button labels with day offsets for date filters and duration for call duration filters
const QUICK_FILTERS = [
    { label: "Today", days: 0, type: "date" },
    { label: "Yesterday", days: 1, type: "date" },
    { label: "Last 3 Days", days: 3, type: "date" },
    { label: "Last 7 Days", days: 7, type: "date" },
    { label: "> 60s", duration: 60, type: "duration" },
];

interface SaharaFiltersProps {
    onFilter: (values: any) => void;
    onReset: () => void;
    loading: boolean;
    initialValues?: any;
}

// Sahara filter panel with quick filter pills and collapsible advanced filters
const SaharaFilters = ({ onFilter, onReset, loading, initialValues }: SaharaFiltersProps) => {
    const [form] = Form.useForm();
    // Track active quick filter label for highlighted pill styling
    const [activeQuick, setActiveQuick] = useState<string | null>(() => {
        return sessionStorage.getItem('sahara_quick_filter') || null;
    });
    // Filter panel is visible by default, can be toggled by clicking filter icon
    const [visible, setVisible] = useState(true);

    // Sync form values with initialValues if provided
    useEffect(() => {
        if (initialValues) {
            const values = { ...initialValues };
            if (values.start_date && values.end_date) {
                values.date_range = [values.start_date, values.end_date];
            }
            form.setFieldsValue(values);
        }
    }, [initialValues, form]);

    // Apply quick filter by computing date range or setting duration and triggering fetch immediately
    const applyQuickFilter = (label: string, days?: number, duration?: number, type?: string) => {
        setActiveQuick(label);
        sessionStorage.setItem('sahara_quick_filter', label);
        setVisible(true);

        // Get current form values
        const current = form.getFieldsValue();

        if (type === "duration") {
            // For duration filters, set the min_duration_sec field and clear date range
            form.setFieldsValue({ min_duration_sec: duration, date_range: undefined });
            onFilter({
                ...current,
                min_duration_sec: duration,
                date_range: undefined,
            });
        } else {
            // For date filters, set date range and preserve other filters
            let startDay: dayjs.Dayjs;
            let endDay: dayjs.Dayjs;

            if (days === 0) {
                // Today: start and end of today
                startDay = dayjs().startOf('day');
                endDay = dayjs();
            } else if (days === 1) {
                // Yesterday: start and end of previous day
                startDay = dayjs().subtract(1, "day").startOf('day');
                endDay = dayjs().subtract(1, "day");
            } else {
                // Last N days: start of N days ago to now
                startDay = dayjs().subtract(days || 0, "day").startOf('day');
                endDay = dayjs();
            }

            form.setFieldsValue({ date_range: [startDay, endDay] });

            // Pass dayjs objects for start/end; SaharaCallTrack formats them before API call
            onFilter({
                ...current,
                date_range: undefined,
                start_date: startDay,
                end_date: endDay,
            });
        }
    };

    // Handle form submission — clear quick filter on manual filter apply
    const handleFinish = (values: any) => {
        setActiveQuick(null);
        sessionStorage.removeItem('sahara_quick_filter');
        const processedValues = { ...values };
        if (values.date_range && values.date_range.length === 2) {
            processedValues.start_date = values.date_range[0];
            processedValues.end_date = values.date_range[1];
            delete processedValues.date_range;
        }
        onFilter(processedValues);
    };

    // Handle reset action — clear quick filter state and notify parent
    const handleReset = () => {
        setActiveQuick(null);
        sessionStorage.removeItem('sahara_quick_filter');
        form.resetFields();
        onReset();
    };

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mb-6">
            {/* Quick filter pill buttons row */}
            <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100">
                <span className="text-xs text-gray-500 font-medium mr-1">Quick:</span>
                {QUICK_FILTERS.map(({ label, days, duration, type }) => (
                    <button
                        key={label}
                        onClick={() => applyQuickFilter(label, days, duration, type)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 cursor-pointer ${activeQuick === label
                            ? "bg-purple-600 text-white border-purple-600"
                            : "bg-white text-gray-600 border-gray-200 hover:border-purple-400 hover:text-purple-600"
                            }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Toggle header bar with filter label and expand/collapse button */}
            <div
                className="flex items-center justify-between px-5 py-3 cursor-pointer select-none hover:bg-gray-50 transition-colors"
                onClick={() => setVisible((v) => !v)}
            >
                <div className="flex items-center gap-2 text-gray-600 font-medium text-sm">
                    <FilterOutlined />
                    <span>Filters</span>
                </div>
                <UpOutlined
                    className="text-gray-400 text-xs transition-transform duration-300"
                    style={{ transform: visible ? "rotate(0deg)" : "rotate(180deg)" }}
                />
            </div>

            {/* Collapsible filter form panel */}
            {visible && (
                <Form
                    form={form}
                    onFinish={handleFinish}
                    layout="vertical"
                    className="px-5 pt-4 pb-3 border-t border-gray-100"
                    size="small"
                >
                    <Row gutter={[16, 4]}>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            {/* UCID filter input */}
                            <Form.Item name="ucid" label="UCID" style={{ marginBottom: 12 }}>
                                <Input
                                    placeholder="Search by UCID"
                                    prefix={<IdcardOutlined className="text-gray-400" />}
                                    allowClear
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            {/* Caller phone filter input */}
                            <Form.Item name="caller_phone" label="E164" style={{ marginBottom: 12 }}>
                                <Input
                                    placeholder="Search by E164"
                                    prefix={<PhoneOutlined className="text-gray-400" />}
                                    allowClear
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
                            {/* Date range picker — clears active quick filter on manual change */}
                            <Form.Item name="date_range" label="Date Range" style={{ marginBottom: 12 }}>
                                <RangePicker
                                    className="w-full"
                                    suffixIcon={<CalendarOutlined className="text-gray-400" />}
                                    onChange={() => {
                                        setActiveQuick(null);
                                        sessionStorage.removeItem('sahara_quick_filter');
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            {/* Min Duration filter select */}
                            <Form.Item name="min_duration_sec" label="Min Duration" style={{ marginBottom: 12 }}>
                                <Select
                                    placeholder="Select Min Duration"
                                    className="w-full"
                                    allowClear
                                    suffixIcon={<ClockCircleOutlined className="text-gray-400" />}
                                >
                                    <Option value={10}>&gt; 10s</Option>
                                    <Option value={30}>&gt; 30s</Option>
                                    <Option value={60}>&gt; 60s</Option>
                                    <Option value={120}>&gt; 2 mins</Option>
                                    <Option value={180}>&gt; 3 mins</Option>
                                    <Option value={240}>&gt; 4 mins</Option>
                                    <Option value={300}>&gt; 5 mins</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Action buttons aligned to the right */}
                    <div className="flex justify-end gap-2 pt-1 pb-1 border-t border-gray-50 mt-2">
                        <Button size="small" icon={<ClearOutlined />} onClick={handleReset}>
                            Reset
                        </Button>
                        <Button
                            size="small"
                            type="primary"
                            htmlType="submit"
                            icon={<SearchOutlined />}
                            loading={loading}
                            className="bg-purple-600 hover:bg-purple-500 border-purple-600"
                        >
                            Filter
                        </Button>
                    </div>
                </Form>
            )}
        </div>
    );
};

export default SaharaFilters;
